import { MoneyInput } from '@/components/ui';
import { IconPlus, IconTrash } from '@/components/Icons';

/** Uma faixa em edição: o % fica como texto para não atrapalhar a digitação. */
export interface TierDraft {
  key: number;
  minCapital: number;
  percent: string;
}

interface TiersEditorProps {
  tiers: TierDraft[];
  onChange: (tiers: TierDraft[]) => void;
  /** Reaplica as faixas a todos os clientes escolhidos, desfazendo os ajustes à mão. */
  onApplyAll: () => void;
  canApply: boolean;
}

let nextKey = 1;
export const newTierKey = () => nextKey++;

/**
 * As faixas da operação: "a partir de R$ X de capital, Y%". Elas sugerem o %
 * de cada cliente escolhido; o ajuste à mão de um cliente vale sobre a faixa.
 */
export function TiersEditor({ tiers, onChange, onApplyAll, canApply }: TiersEditorProps) {
  const update = (key: number, patch: Partial<TierDraft>) =>
    onChange(tiers.map((tier) => (tier.key === key ? { ...tier, ...patch } : tier)));

  function add() {
    const last = tiers[tiers.length - 1];
    onChange([...tiers, { key: newTierKey(), minCapital: last ? last.minCapital + 100_000 : 0, percent: '' }]);
  }

  return (
    <section className="lev-block" aria-labelledby="lev-faixas">
      <div className="lev-block-head">
        <span id="lev-faixas" className="lev-block-title">Faixas de %</span>
        <span className="muted lev-block-hint">Sugerem o % de cada cliente pelo capital aportado.</span>
        <div className="spacer" />
        {tiers.length > 0 && canApply ? (
          <button type="button" className="link-btn lev-block-hint" onClick={onApplyAll}>Aplicar a todos os escolhidos</button>
        ) : null}
      </div>

      {tiers.length === 0 ? (
        <p className="muted lev-empty-line">Sem faixas: o % de cada cliente vai à mão.</p>
      ) : (
        <ol className="lev-tiers">
          {tiers.map((tier, index) => (
            <li key={tier.key} className="lev-tier">
              <span className="lev-tier-label muted">{index === 0 ? 'Capital a partir de' : 'A partir de'}</span>
              <div className="lev-tier-money" aria-label={`Capital mínimo da faixa ${index + 1}`}>
                <MoneyInput value={tier.minCapital} onChange={(cents) => update(tier.key, { minCapital: cents })} placeholder="0,00" />
              </div>
              <span className="lev-tier-arrow muted" aria-hidden>→</span>
              <div className="lev-pct">
                <input
                  className="inp num"
                  inputMode="decimal"
                  value={tier.percent}
                  onChange={(event) => update(tier.key, { percent: event.target.value.replace(/[^\d.,]/g, '') })}
                  placeholder="0"
                  aria-label={`% da faixa ${index + 1}`}
                />
                <span className="lev-pct-sign" aria-hidden>%</span>
              </div>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Tirar a faixa ${index + 1}`}
                onClick={() => onChange(tiers.filter((item) => item.key !== tier.key))}
              >
                <IconTrash />
              </button>
            </li>
          ))}
        </ol>
      )}

      <button type="button" className="btn btn-sm btn-ghost" onClick={add}>
        <IconPlus /> Faixa
      </button>
    </section>
  );
}
